import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { ArrowLeft, Clock, Check, ExternalLink, Phone, Globe } from "lucide-react";
import { Button } from "../components/ui/button";

export default function TourDetail() {
  const { id } = useParams();
  const [tour, setTour] = useState(null);

  useEffect(() => {
    api.get(`/tours/${id}`).then((r) => setTour(r.data));
  }, [id]);

  if (!tour) return <Layout><div className="max-w-7xl mx-auto px-6 py-20">Loading…</div></Layout>;
  const op = tour.operator;

  return (
    <Layout>
      <div className="relative h-[55vh] overflow-hidden">
        <img src={tour.image} alt={tour.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute top-6 left-6">
          <Link to="/directory/activity" className="bg-white/90 backdrop-blur rounded-full px-4 py-2 text-sm text-navy inline-flex items-center gap-2 hover:bg-white transition-colors" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> Play
          </Link>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-7xl mx-auto">
            <div className="text-xs uppercase tracking-widest text-white/80 mb-2" data-testid="tour-operator-pill">
              by {op?.name || "Local operator"}
            </div>
            <h1 className="font-heading text-4xl lg:text-6xl text-white tracking-tight" data-testid="tour-name">{tour.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-white/90 mt-3 text-sm">
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {tour.duration_label}</span>
              <span>·</span>
              <span className="text-xl text-white font-medium">From ${Math.round(tour.price_from)} {tour.currency}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <h2 className="font-heading text-2xl text-navy mb-4">About this experience</h2>
          <p className="text-neutral-700 leading-relaxed text-lg mb-10" data-testid="tour-description">{tour.description}</p>

          {tour.highlights?.length > 0 && (
            <div className="mb-10" data-testid="tour-highlights">
              <h3 className="font-heading text-xl text-navy mb-4">What's included</h3>
              <ul className="space-y-2">
                {tour.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-neutral-700">
                    <Check className="w-5 h-5 text-coastal shrink-0 mt-0.5" /> <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <aside>
          <div className="bg-white border border-black/5 rounded-2xl p-6 sticky top-24" data-testid="tour-book-card">
            <div className="text-3xl font-heading text-navy">From ${Math.round(tour.price_from)}</div>
            <div className="text-xs text-neutral-500 mb-5">{tour.currency} · per person</div>
            <a href={tour.booking_url} target="_blank" rel="noreferrer">
              <Button className="w-full rounded-full bg-navy hover:bg-navy/90" data-testid="book-tour-btn">
                Book with {op?.name || "operator"} <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <p className="text-xs text-neutral-400 mt-3 text-center">
              You'll book directly with the operator. inAirlie doesn't charge a booking fee.
            </p>
            {op && (
              <div className="mt-6 pt-6 border-t border-black/5">
                <div className="text-xs uppercase tracking-widest text-neutral-400 mb-2">Operator</div>
                <Link to={`/venue/${op.id}`} className="font-heading text-lg text-navy hover:text-coastal block">
                  {op.name}
                </Link>
                {op.rating > 0 && (
                  <div className="text-xs text-neutral-500 mt-1">★ {op.rating.toFixed(1)} ({op.review_count} reviews)</div>
                )}
                <div className="mt-3 space-y-1 text-sm">
                  {op.phone && <a href={`tel:${op.phone}`} className="flex items-center gap-2 text-neutral-700 hover:text-navy"><Phone className="w-3.5 h-3.5" /> {op.phone}</a>}
                  {op.website && <a href={op.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-neutral-700 hover:text-navy"><Globe className="w-3.5 h-3.5" /> Website</a>}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </Layout>
  );
}
